import React, { memo } from 'react';
import isEqual from 'lodash.isequal';
import getSuitCSSClasses from '@/utils/getSuitCSSClasses';
import { Box } from '@hz-design-system/web-ui';
// import { imageFormatter, opvalStickerFormatter } from '@/client/utils/formatters';
import { Attribute } from '@/client/types/Listings';
import { TImage } from '@/client/types/Images';

import { ListingOtherSeller } from '@/client/components/ListingsNew/components';
import ListingListViewContentDesktop from './ListingListViewDesktop/ListingListViewContentDesktop';
import ListingListViewContentMweb from './ListingListViewMweb/ListingListViewContentMweb';
import ListingListViewContentTablet from './ListingListViewTablet/ListingListViewContentTablet';
import { getCarPassUrl, getEnergyLabel, getMileage, getRegularAttributes } from './utils/attributes';
import { getIsVerifiedSeller } from './utils/isSellerVerified';

type TListingListViewContentHorizontal = {
  attributes: Attribute[];
  categories?: TCategories;
  categorySpecificDescription?: string;
  correlationId?: string;
  date: string;
  decodingType?: 'sync' | 'async' | 'auto';
  description: string;
  extendedAttributes?: Attribute[];
  fetchPriority?: 'auto' | 'high' | 'low';
  isBottomBlock?: boolean;
  isFavorite?: boolean;
  loadingType?: 'eager' | 'lazy';
  location: { label: string; distance?: string };
  napAvailable?: boolean;
  onFavorite: () => void;
  opvalStickerText?: string;
  otherSellerListings: TOtherSellerListings;
  otherSellerUrl?: string;
  pageLocation?: string;
  pictures?: TImage[];
  price: string;
  priorityProduct?: string;
  sellerInformation?: TSellerInformation;
  shouldDisplayExtendedContent?: boolean;
  thinContent?: boolean;
  title: string;
  trackingData?: string;
  trustIndicators?: { value?: string; key: string }[];
  urgencyFeatureActive?: boolean;
  verticals?: string[];
  vipUrl: string;
};

const ListingListViewContentHorizontal = ({
  attributes = [],
  categories,
  categorySpecificDescription,
  correlationId,
  date,
  decodingType,
  description,
  extendedAttributes = [],
  fetchPriority,
  isBottomBlock = false,
  isFavorite = false,
  loadingType,
  location,
  napAvailable = false,
  onFavorite,
  opvalStickerText,
  otherSellerListings,
  otherSellerUrl,
  pageLocation,
  pictures,
  price,
  priorityProduct = '',
  sellerInformation,
  shouldDisplayExtendedContent = false,
  thinContent,
  title,
  trackingData,
  trustIndicators = [],
  urgencyFeatureActive = false,
  verticals = [],
  vipUrl,
}: TListingListViewContentHorizontal) => {
  const carPassUrl = getCarPassUrl(attributes);
  const energyLabel = getEnergyLabel(attributes);
  const mileage = getMileage(attributes);
  const isVerifiedSeller = getIsVerifiedSeller(sellerInformation, verticals);
  const regularAttributes = getRegularAttributes(attributes);

  return (
    <div
      className={getSuitCSSClasses({
        namespace: 'hz',
        name: 'Listing-listview-content-horizontal',
      })}
    >
      <div
        className={getSuitCSSClasses({
          namespace: 'hz',
          name: 'Listing-item-wrapper-new',
        })}
      >
        <Box as="div" name="Listing-listview-content--desktop">
          <ListingListViewContentDesktop
            carPassUrl={carPassUrl}
            categories={categories}
            correlationId={correlationId}
            date={date}
            decodingType={decodingType}
            description={description}
            energyLabel={energyLabel}
            extendedAttributes={extendedAttributes}
            fetchPriority={fetchPriority}
            isBottomBlock={isBottomBlock}
            isFavorite={isFavorite}
            isVerifiedSeller={isVerifiedSeller}
            loadingType={loadingType}
            location={location}
            mileage={mileage}
            napAvailable={napAvailable}
            onFavorite={onFavorite}
            opvalStickerText={opvalStickerText}
            pageLocation={pageLocation}
            pictures={pictures}
            price={price}
            priorityProduct={priorityProduct}
            regularAttributes={regularAttributes}
            sellerInformation={sellerInformation}
            thinContent={thinContent}
            title={title}
            trackingData={trackingData}
            trustIndicators={trustIndicators}
            urgencyFeatureActive={urgencyFeatureActive}
            vipUrl={vipUrl}
          />
        </Box>

        <Box as="div" name="Listing-listview-content--tablet">
          <ListingListViewContentTablet
            carPassUrl={carPassUrl}
            categories={categories}
            correlationId={correlationId}
            date={date}
            decodingType={decodingType}
            description={description}
            energyLabel={energyLabel}
            extendedAttributes={extendedAttributes}
            fetchPriority={fetchPriority}
            isFavorite={isFavorite}
            isVerifiedSeller={isVerifiedSeller}
            loadingType={loadingType}
            location={location}
            mileage={mileage}
            napAvailable={napAvailable}
            onFavorite={onFavorite}
            opvalStickerText={opvalStickerText}
            pageLocation={pageLocation}
            pictures={pictures}
            price={price}
            priorityProduct={priorityProduct}
            regularAttributes={regularAttributes}
            sellerInformation={sellerInformation}
            thinContent={thinContent}
            title={title}
            trackingData={trackingData}
            trustIndicators={trustIndicators}
            urgencyFeatureActive={urgencyFeatureActive}
            vipUrl={vipUrl}
          />
        </Box>

        <Box as="div" name="Listing-listview-content--mweb">
          <ListingListViewContentMweb
            carPassUrl={carPassUrl}
            categories={categories}
            categorySpecificDescription={categorySpecificDescription}
            correlationId={correlationId}
            date={date}
            decodingType={decodingType}
            extendedAttributes={extendedAttributes}
            fetchPriority={fetchPriority}
            isFavorite={isFavorite}
            loadingType={loadingType}
            location={location}
            napAvailable={napAvailable}
            onFavorite={onFavorite}
            opvalStickerText={opvalStickerText}
            pageLocation={pageLocation}
            pictures={pictures}
            price={price}
            priorityProduct={priorityProduct}
            sellerInformation={sellerInformation}
            shouldDisplayExtendedContent={shouldDisplayExtendedContent}
            thinContent={thinContent}
            title={title}
            trackingData={trackingData}
            trustIndicators={trustIndicators}
            urgencyFeatureActive={urgencyFeatureActive}
            vipUrl={vipUrl}
          />
        </Box>
      </div>

      {otherSellerListings && otherSellerUrl && otherSellerListings.totalSellerListingsResults > 1 && (
        <ListingOtherSeller
          otherSellerListings={otherSellerListings}
          pageLocation={pageLocation}
          correlationId={correlationId}
          url={otherSellerUrl}
        />
      )}
    </div>
  );
};

// todo: find which properties are really needs to be checked for preventing rerendering
function arePropsEqual(prevProps, nextProps) {
  return isEqual(prevProps, nextProps);
}

export default memo(ListingListViewContentHorizontal, arePropsEqual);
